import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Scale, FileText, Shield, Mail, MapPin, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const LegalNotice = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
              </div>
              <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {isFr ? 'Retour à l\'accueil' : 'Back to home'}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Scale className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {isFr ? 'Mentions Légales' : 'Legal Notice'}
          </h1>
        </div>

        <div className="space-y-8">
          {/* Éditeur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                {isFr ? 'Éditeur du site' : 'Website Publisher'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">Moto Social</strong><br />
                {isFr ? 'Plateforme de réseau social pour passionnés de moto' : 'Social network platform for motorcycle enthusiasts'}
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Forme juridique' : 'Legal form'}:</strong><br />
                SASU (Société par Actions Simplifiée Unipersonnelle)
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Siège social' : 'Headquarters'}:</strong><br />
                55 rue du Four Saint-François<br />
                66000 Perpignan<br />
                France
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Contact' : 'Contact'}:</strong><br />
                <a href="mailto:contact@motosocial.eu" className="text-primary hover:underline">
                  contact@motosocial.eu
                </a>
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Directeur de la publication' : 'Publication Director'}:</strong><br />
                Kurt
              </p>
            </CardContent>
          </Card>

          {/* Hébergeur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                {isFr ? 'Hébergement' : 'Hosting'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">Emergent</strong><br />
                {isFr ? 'Service d\'hébergement cloud' : 'Cloud hosting service'}<br />
                <a href="https://emergent.sh" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  https://emergent.sh
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Propriété intellectuelle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {isFr ? 'Propriété intellectuelle' : 'Intellectual Property'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr 
                  ? "L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, logiciels, etc.) est protégé par le droit d'auteur et le droit des marques. Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site est interdite sans autorisation écrite préalable."
                  : "All content on this site (text, images, videos, logos, icons, sounds, software, etc.) is protected by copyright and trademark law. Any reproduction, representation, modification, publication, or adaptation of all or part of the site elements is prohibited without prior written authorization."}
              </p>
              <p>
                {isFr
                  ? "Les marques et logos reproduits sur ce site sont déposés par les sociétés qui en sont propriétaires."
                  : "The trademarks and logos reproduced on this site are registered by their respective owners."}
              </p>
            </CardContent>
          </Card>

          {/* Responsabilité */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {isFr ? 'Limitation de responsabilité' : 'Limitation of Liability'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Moto Social s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, Moto Social ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site."
                  : "Moto Social strives to ensure the accuracy and updating of information published on this site. However, Moto Social cannot guarantee the accuracy, precision, or completeness of the information made available on this site."}
              </p>
              <p>
                {isFr
                  ? "Moto Social décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur ce site."
                  : "Moto Social disclaims any liability for any inaccuracy, inexactitude, or omission relating to information available on this site."}
              </p>
            </CardContent>
          </Card>

          {/* Liens */}
          <Card>
            <CardHeader>
              <CardTitle>{isFr ? 'Liens hypertextes' : 'Hyperlinks'}</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                {isFr
                  ? "Ce site peut contenir des liens hypertextes vers d'autres sites. Moto Social n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu."
                  : "This site may contain hyperlinks to other sites. Moto Social has no control over these sites and disclaims any responsibility for their content."}
              </p>
            </CardContent>
          </Card>

          {/* Date de mise à jour */}
          <p className="text-sm text-muted-foreground text-center pt-8">
            {isFr ? 'Dernière mise à jour' : 'Last updated'}: {new Date().toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>
    </div>
  );
};

export default LegalNotice;
